import { getDay } from "date-fns";

import { readImageContent } from "./images";

type Validator = (value: any) => string | Promise<string>;

const DAYS = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

export const compose = (...validators: Array<Validator>) => (value: any) => {
  let result;
  for (let index = 0; index < validators.length; index++) {
    result = validators[index](value);
    if (result) return result;
  }
  return "";
};

export const emptyValidator = (value: any) => {
  return Number.isFinite(value) ||
    Boolean(typeof value === "string" ? value.trim() : value)
    ? ""
    : "This field is required.";
};

export const emptyArrayValidator = (validator: Validator) => (
  values: Array<any>,
): string => {
  return (Array.isArray(values) && values.length > 0) ||
    (!Array.isArray(values) && validator(values) === "")
    ? ""
    : "This field is required.";
};

export const lengthValidator = (maxLength = 1) => (value = "") =>
  value && value.length > maxLength
    ? `The length must be ${maxLength} characters or fewer.`
    : "";

export const imageTypesValidator = (type: string, acceptedTypes: string) => {
  if (type) {
    if (!acceptedTypes.includes(type.split("/")?.[1])) {
      const mappedTypes = acceptedTypes.replace(/,/g, ", ").replace(/\./g, "");

      return `Incorrect format. Available formats: ${mappedTypes}`;
    }
  }

  return "";
};

export const imageIsCorruptedValidator = async (image?: File | string) => {
  if (image && typeof image !== "string") {
    const reader = new FileReader();
    const { error } = await readImageContent(reader, image);
    console.log(error);

    return error || "";
  }

  return "";
};

export const fileSizeValidator = (sizeInMb: number) => (value?: File) => {
  const sizeInBytes = sizeInMb * 1024 * 1024;

  return value && value.size > sizeInBytes
    ? `Uploaded file exceeds the file size limit: ${sizeInMb} MB`
    : "";
};

export const startDateDayOfWeekValidator = (dayNumber: number) => (
  date: Date,
) => {
  return getDay(date) !== dayNumber
    ? `The week should start on ${DAYS[dayNumber]}.`
    : "";
};

export const recipeDurationValidator = ({
  hours,
  minutes,
}: {
  hours: number | "";
  minutes: number | "";
}) => {
  if (hours === "" && minutes === "") {
    return "This field is required.";
  }

  if (![hours, minutes].filter(Boolean).length) {
    return "Please specify duration.";
  }

  return minutes >= 60 ? "The minutes value should be less than 60." : "";
};

export const emailValidator = (email: string) => {
  // See https://emailregex.com
  const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

  if (!email || email.length <= 0) return "";
  if (!re.test(email)) return "A valid email address is required.";

  return "";
};

export const numberFormatValidator = (value = "") => {
  if (!value.length) {
    return "";
  }

  const isDecimal = value.toString().indexOf(".") !== -1;

  if (isDecimal) {
    const numbers = value.toString().split(".");

    if (numbers.length > 2 || !numbers[0].length || !numbers[1].length) {
      return "Incorrect number format.";
    }
  }

  return "";
};

export const decimalNumberLengthValidator = (maxLength = 1) => (value = "") =>
  value.toString().replace(/\D/g, "").length > maxLength
    ? `The length must be ${maxLength} digits or fewer.`
    : "";

export const decimalNumberDecimalPlacesLengthValidator = (maxLength = 1) => (
  value = "",
) =>
  value.toString().replace(/^\d*./g, "").length > maxLength
    ? `Incorrect format - ${maxLength} decimal places max.`
    : "";

export const urlValidator = (url: string = "") => {
  const urlRegex = /^(http|https):\/\/(\S+)$/gi;

  if (!url) {
    return "";
  }

  if (!urlRegex.test(url)) return "A valid url address is required.";

  return "";
};
